import { FC, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Modal from 'views/components/UI/Modal'
import CloseIcon from '@mui/icons-material/Close'
import { Button } from 'views/components/UI/Buttons'
import { H4, H5 } from 'views/components/UI/Text'
import { ModalBody, ModalContainer, ModalHeader, AnchorSection } from '../styled'
import File from 'views/components/assets/csv.png'
import DownloadIcon from 'assets/icons/DownloadIcon'
import FileFolderIcon from 'assets/icons/fileFolderIcon'
import DeleteIcon from 'assets/icons/DeleteIcon'
import GreenFile from 'assets/icons/greenFile'
import { useNotification } from 'lib/context/notification.context'
import { User, EvaluatorProps } from '../constants'
import { platformAdminRoutes } from 'router/routes'
import { Row } from 'views/components/UI/Row'
import { handleDragEnter, handleDragLeave, handleDragOver, handleDrop } from './uploadHandlers'
import * as XLSX from 'xlsx'
import { generalDataSelector } from 'ducks/auth'
import { IDocumentType } from 'types/common'
import { useTranslation } from 'react-i18next'
import { selectTest360Data } from 'ducks/test360'
import { VALIDATE_EMAIL_REGEX } from 'constants/regex.constants'

export interface Test360UsersAndEvaluators {
  users: User[]
  evaluators: EvaluatorProps[]
}

interface DisclaimerModalProps {
  openModal: boolean
  setOpenModal: (value: boolean) => void
  setAddedUsers: (value: User[]) => void
  setUploadedbyFile: (value: boolean) => void
  setTryUploadAgain?: (value: boolean) => void
  setAddTest360FromExcel?: (value: Test360UsersAndEvaluators) => void
  canAddUsers?: boolean
  showStepTwo?: boolean
  addedEvaluators?: EvaluatorProps[]
  addedUsers360?: User[]
}

const UploadModal: FC<DisclaimerModalProps> = ({
  openModal,
  setOpenModal,
  setAddedUsers,
  setAddTest360FromExcel,
  setUploadedbyFile,
  setTryUploadAgain,
  canAddUsers = false,
  showStepTwo = false,
  addedEvaluators,
  addedUsers360,
}) => {
  const { t } = useTranslation()
  const { getError, getWarning } = useNotification()
  const navigate = useNavigate()
  const { data: generalData } = useSelector(generalDataSelector)
  const test360Data = useSelector(selectTest360Data)
  const documentTypeOptions = generalData?.document_type

  const [stepOne, setStepOne] = useState(true)
  const [stepTwo, setStepTwo] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [fileList, setFileList] = useState<File[]>([])
  const [fileExist, setFileExist] = useState(false)

  const [users, setUsers] = useState<User[]>([])
  const [evaluators, setEvaluators] = useState<EvaluatorProps[]>([])

  // Roles válidos para evaluadores
  const ROLE_TYPES = [
    {
      name: 'Evaluado',
    },
    {
      name: 'Jefe',
    },
    {
      name: 'Par',
    },
    {
      name: 'Colaborador',
    },
    {
      name: 'Cliente Interno',
    },
    {
      name: 'Cliente Externo',
    },
    {
      name: 'Otro',
    },
  ]

  // Función para validar email
  const isValidEmail = (email: string): boolean => {
    return VALIDATE_EMAIL_REGEX.test(email)
  }

  const isValidName = (name: string): boolean => {
    if (!name || typeof name !== 'string') return false
    const trimmedName = name.trim()
    if (!trimmedName) return false

    return /^[a-zA-ZÀ-ÿÑñ\s]+$/.test(trimmedName)
  }

  // Función para capitalizar cada palabra (primera letra mayúscula, resto minúscula)
  const capitalizeWords = (text: string): string => {
    if (!text || typeof text !== 'string') return ''
    return text
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return ''
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')
  }

  // Función para formatear email a minúsculas
  const formatEmail = (email: string): string => {
    if (!email || typeof email !== 'string') return ''
    return email.trim().toLowerCase()
  }

  useEffect(() => {
    if (fileList.length > 0) {
      let usersFromExcel: any[] = []
      const reader = new FileReader()
      if (!test360Data.length) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheet = workbook.SheetNames[0]
          const sheet = workbook.Sheets[firstSheet]
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
          usersFromExcel = jsonData.map((user: any, index: number) => {
            return {
              index,
              user: {
                document_type: documentTypeOptions?.find((documentType: IDocumentType) => documentType.name === user[0])
                  ?.id,
                document: user[1]?.toString() || '',
                email: user[2],
                first_name: user[3],
                last_name: user[4],
              },
            }
          })

          const filteredUsers = usersFromExcel
            .filter((user: any, idx: number) => {
              // Skip header row (fila 1 con encabezados)
              if (idx === 0) return false

              // Verificar que la fila tenga datos (no esté vacía)
              const hasData = user.user.email || user.user.first_name || user.user.last_name || user.user.document

              if (!hasData) return false

              // Validaciones básicas - solo filtrar usuarios que tengan los campos requeridos válidos
              const hasValidEmail = user.user.email && isValidEmail(user.user.email.toString().trim())
              const hasValidFirstName = user.user.first_name && isValidName(user.user.first_name.toString().trim())
              const hasValidLastName = user.user.last_name && isValidName(user.user.last_name.toString().trim())

              return hasValidEmail && hasValidFirstName && hasValidLastName
            })
            .map((item) => {
              const newUser = { ...item.user }
              // Limpiar y formatear los datos
              ;(Object.keys(newUser) as Array<keyof typeof newUser>).forEach((key) => {
                if (typeof newUser[key] === 'string') {
                  if (key === 'document') {
                    newUser[key] = newUser[key].replace(/\s+/g, '')
                  } else if (key === 'email') {
                    newUser[key] = formatEmail(newUser[key])
                  } else if (key === 'first_name' || key === 'last_name') {
                    newUser[key] = capitalizeWords(newUser[key])
                  } else {
                    newUser[key] = newUser[key].trim()
                  }
                }
              })
              return { ...item, user: newUser }
            })

          // Combinar usuarios existentes con nuevos, evitando duplicados por email
          const existingUserEmails = new Set(users.map((user: any) => formatEmail((user.user || user).email || '')))
          const newUsers = filteredUsers.filter((newUser: any) => !existingUserEmails.has(newUser.user.email))

          // Contar usuarios procesados vs usuarios válidos
          const totalRows = usersFromExcel.length - 1 // Excluir header
          const validUsers = filteredUsers.length
          const skippedUsers = totalRows - validUsers

          if (skippedUsers > 0) {
            const warningMessage = `Se procesaron ${validUsers} usuario${validUsers !== 1 ? 's' : ''} válido${
              validUsers !== 1 ? 's' : ''
            }.`
            getWarning(warningMessage)
          }

          setUsers([...users, ...newUsers] as User[])
        }
      } else {
        reader.onload = (e: any) => {
          // Validar que el archivo sea Excel como primera validación
          if (fileList.length > 0) {
            const file = fileList[0]
            const fileExtension = file.name.split('.').pop()?.toLowerCase()

            if (fileExtension !== 'xlsx' && fileExtension !== 'xls' && fileExtension !== 'xlsm') {
              getError(
                'Solo se permiten archivos de Excel (.xlsx, .xls, .xlsm). Por favor, selecciona un archivo con la extensión correcta.'
              )
              // Limpiar el archivo del modal
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
              setFileList([])
              setUsers([])
              setEvaluators([])
              setFileExist(false)
              return
            }
          }

          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheet = workbook.SheetNames[0]
          const sheet = workbook.Sheets[firstSheet]
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][]

          // Validar que el archivo no esté completamente vacío
          if (jsonData.length < 3) {
            getWarning(
              'El archivo está completamente vacío o no contiene suficientes filas. Por favor, asegúrate de descargar la plantilla y vuelve a intentarlo.'
            )
            // Limpiar el archivo del modal
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            setFileList([])
            setUsers([])
            setEvaluators([])
            setFileExist(false)
            return
          }

          // Validar formato del archivo Excel para 360
          if (jsonData.length >= 2) {
            const headerRow = jsonData[0] as any[]
            const subHeaderRow = jsonData[1] as any[]

            // Verificar que tenga al menos las 4 columnas básicas del usuario evaluado (nombre + apellido + email + cargo)
            if (!headerRow || headerRow.length < 4) {
              getWarning(
                'El archivo no tiene el formato esperado para evaluación 360°. Por favor, descarga la plantilla correcta y vuelve a intentarlo.'
              )
              // Limpiar el archivo del modal
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
              setFileList([])
              setUsers([])
              setEvaluators([])
              setFileExist(false)
              return
            }

            // Verificar que los encabezados tengan una estructura esperada para evaluación 360
            // Los primeros encabezados deben estar relacionados con usuario evaluado
            const firstHeaderText = headerRow[0]?.toString().toLowerCase() || ''
            const secondHeaderText = headerRow[1]?.toString().toLowerCase() || ''
            const thirdHeaderText = headerRow[2]?.toString().toLowerCase() || ''
            const fourthHeaderText = headerRow[3]?.toString().toLowerCase() || ''

            // Verificar encabezados de la segunda fila (títulos específicos)
            const firstSubHeader = subHeaderRow[0]?.toString().toLowerCase() || ''
            const secondSubHeader = subHeaderRow[1]?.toString().toLowerCase() || ''
            const thirdSubHeader = subHeaderRow[2]?.toString().toLowerCase() || ''
            const fourthSubHeader = subHeaderRow[3]?.toString().toLowerCase() || ''

            // Validar que contenga términos relacionados con evaluación 360
            const hasUserRelatedHeaders =
              (firstHeaderText.includes('evaluado') ||
                firstHeaderText.includes('usuario') ||
                firstSubHeader.includes('nombre')) &&
              (secondSubHeader.includes('apellido') || secondHeaderText.includes('apellido')) &&
              (thirdSubHeader.includes('email') ||
                thirdSubHeader.includes('correo') ||
                thirdHeaderText.includes('email') ||
                thirdHeaderText.includes('correo')) &&
              (fourthSubHeader.includes('cargo') ||
                fourthSubHeader.includes('rol') ||
                fourthHeaderText.includes('cargo') ||
                fourthHeaderText.includes('rol'))

            // Verificar que después de las 4 primeras columnas haya estructura de evaluadores
            let hasEvaluatorStructure = true
            if (headerRow.length > 4) {
              // Verificar que los encabezados posteriores contengan términos de evaluadores
              const evaluatorHeaders = headerRow.slice(4, Math.min(52, headerRow.length)) // Revisar las siguientes 48 columnas para 12 evaluadores
              const evaluatorHeadersText = evaluatorHeaders.join(' ').toLowerCase()

              hasEvaluatorStructure =
                evaluatorHeadersText.includes('evaluador') ||
                evaluatorHeadersText.includes('rol') ||
                evaluatorHeadersText.includes('jefe') ||
                evaluatorHeadersText.includes('par') ||
                evaluatorHeadersText.includes('subordinado')
            }

            if (!hasUserRelatedHeaders || !hasEvaluatorStructure) {
              getWarning(
                'El archivo no tiene el formato esperado para evaluación 360°. Por favor, descarga la plantilla correcta y vuelve a intentarlo.'
              )
              // Limpiar el archivo del modal
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
              setFileList([])
              setUsers([])
              setEvaluators([])
              setFileExist(false)
              return
            }
          }

          // Validar que el archivo no esté vacío (revisar todas las filas de datos)
          let hasValidUserData = false
          for (let rowIndex = 2; rowIndex < jsonData.length; rowIndex++) {
            const row = jsonData[rowIndex] as any[]
            if (row && row.length >= 4) {
              // Verificar si hay datos en las primeras 4 columnas (usuario evaluado)
              const firstName = row[0]
              const lastName = row[1]
              const email = row[2]
              const evaluatedRole = row[3]

              if (firstName || lastName || email || evaluatedRole) {
                hasValidUserData = true
                break
              }
            }
          }

          if (!hasValidUserData) {
            getWarning(
              'El archivo está vacío o no contiene datos válidos. Por favor, asegúrate de llenar los datos y vuelve a intentarlo.'
            )
            // Limpiar el archivo del modal
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            setFileList([])
            setUsers([])
            setEvaluators([])
            setFileExist(false)
            return
          }

          const evaluatedUsers: any[] = []
          const evaluatorUsers: any[] = []
          const usersExceedingEvaluatorLimit: string[] = []

          // Track validation errors for summary
          const validationErrors = {
            usersWithMissingEmail: 0,
            usersWithMissingFirstName: 0,
            usersWithMissingLastName: 0,
            usersWithMissingEvaluatedRole: 0,
            usersWithInvalidEmail: 0,
            usersWithInvalidFirstName: 0,
            usersWithInvalidLastName: 0,
            usersWithoutValidEvaluators: 0,
            evaluatorsWithMissingFirstName: 0,
            evaluatorsWithMissingLastName: 0,
            evaluatorsWithMissingEmail: 0,
            evaluatorsWithMissingRole: 0,
            evaluatorsWithInvalidEmail: 0,
            evaluatorsWithInvalidFirstName: 0,
            evaluatorsWithInvalidLastName: 0,
          }

          for (let rowIndex = 2; rowIndex < jsonData.length; rowIndex++) {
            const row = jsonData[rowIndex] as any[]
            if (!row || row.length < 4) {
              continue
            }
            const fullRow: any[] = []
            for (let i = 0; i < 52; i++) {
              fullRow[i] = row[i] !== undefined ? row[i] : null
            }

            // En la posición 0 está el nombre, en la 1 el apellido, en la 2 el email y en la 3 el cargo del evaluado
            // Eliminar espacios en blanco al inicio y final
            const firstName = fullRow[0] ? fullRow[0].toString().trim() : fullRow[0]
            const lastName = fullRow[1] ? fullRow[1].toString().trim() : fullRow[1]
            const email = fullRow[2] ? fullRow[2].toString().trim() : fullRow[2]
            const evaluatedRole = fullRow[3] ? fullRow[3].toString().trim() : fullRow[3]

            // Validar datos del usuario evaluado
            let userIsValid = true

            if (!email) {
              validationErrors.usersWithMissingEmail++
              userIsValid = false
            } else if (!isValidEmail(email)) {
              validationErrors.usersWithInvalidEmail++
              userIsValid = false
            }

            if (!firstName) {
              validationErrors.usersWithMissingFirstName++
              userIsValid = false
            } else if (!isValidName(firstName?.toString())) {
              validationErrors.usersWithInvalidFirstName++
              userIsValid = false
            }

            if (!lastName) {
              validationErrors.usersWithMissingLastName++
              userIsValid = false
            } else if (!isValidName(lastName?.toString())) {
              validationErrors.usersWithInvalidLastName++
              userIsValid = false
            }

            if (!evaluatedRole) {
              validationErrors.usersWithMissingEvaluatedRole++
              userIsValid = false
            }

            const userEvaluators: any[] = []
            let hasValidEvaluator = false
            const userEvaluatorEmails = new Set<string>() // Para trackear evaluadores duplicados por email

            // Contar evaluadores válidos para este usuario
            let validEvaluatorCount = 0

            for (let evalPos = 0; evalPos < 12; evalPos++) {
              // Son 4 campos por evaluador: rol, nombre, apellido y correo
              const startIdx = 4 + evalPos * 4

              // Eliminar espacios en blanco al inicio y final de los datos del evaluador
              const role = fullRow[startIdx] ? fullRow[startIdx].toString().trim() : fullRow[startIdx]
              const evFirstName = fullRow[startIdx + 1]
                ? fullRow[startIdx + 1].toString().trim()
                : fullRow[startIdx + 1]
              const evLastName = fullRow[startIdx + 2] ? fullRow[startIdx + 2].toString().trim() : fullRow[startIdx + 2]
              const evEmail = fullRow[startIdx + 3] ? fullRow[startIdx + 3].toString().trim() : fullRow[startIdx + 3]

              // Verificar si este evaluador tiene algún dato (no está completamente vacío)
              const hasAnyData = role || evFirstName || evLastName || evEmail

              // Si no hay ningún dato, ignoramos este evaluador (no es un error, está en blanco)
              if (!hasAnyData) {
                continue
              }

              // Contar este evaluador como válido (tiene al menos un dato)
              validEvaluatorCount++

              // Si tiene algún dato, entonces validamos que tenga todos los campos requeridos
              let evaluatorIsValid = true

              if (!evEmail) {
                validationErrors.evaluatorsWithMissingEmail++
                evaluatorIsValid = false
              } else if (!isValidEmail(evEmail)) {
                validationErrors.evaluatorsWithInvalidEmail++
                evaluatorIsValid = false
              }

              if (!role) {
                validationErrors.evaluatorsWithMissingRole++
                evaluatorIsValid = false
              } else {
                // Validar que el rol esté en la lista de roles permitidos
                const validRoleNames = ROLE_TYPES.map((roleType) => roleType.name)
                if (!validRoleNames.includes(role.toString())) {
                  getWarning(
                    `Se encontró un rol no soportado: "${role}". Los roles válidos son: ${validRoleNames.join(', ')}`
                  )
                  // Limpiar el archivo del modal y cancelar la subida
                  if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                  }
                  setFileList([])
                  setUsers([])
                  setEvaluators([])
                  setFileExist(false)
                  return
                }
              }

              if (!evFirstName) {
                validationErrors.evaluatorsWithMissingFirstName++
                evaluatorIsValid = false
              } else if (!isValidName(evFirstName.toString())) {
                validationErrors.evaluatorsWithInvalidFirstName++
                evaluatorIsValid = false
              }

              if (!evLastName) {
                validationErrors.evaluatorsWithMissingLastName++
                evaluatorIsValid = false
              } else if (!isValidName(evLastName.toString())) {
                validationErrors.evaluatorsWithInvalidLastName++
                evaluatorIsValid = false
              }

              if (!evaluatorIsValid) {
                continue
              }

              // Validar que no haya evaluadores duplicados por email para el mismo usuario
              const formattedEvEmail = formatEmail(evEmail)
              if (userEvaluatorEmails.has(formattedEvEmail)) {
                getWarning('Hay un evaluador asignado más de una vez al mismo evaluado, por favor revisa tu plantilla')
                // Limpiar el archivo del modal y cancelar la subida
                if (fileInputRef.current) {
                  fileInputRef.current.value = ''
                }
                setFileList([])
                setUsers([])
                setEvaluators([])
                setFileExist(false)
                return
              }

              // Agregar el email del evaluador al set para futuras verificaciones
              userEvaluatorEmails.add(formattedEvEmail)

              // Si el evaluador tiene todos los datos requeridos, es válido
              hasValidEvaluator = true

              const evaluator: any = {
                role: capitalizeWords(role || ''),
                first_name: capitalizeWords(evFirstName.toString() || ''),
                last_name: capitalizeWords(evLastName.toString() || ''),
                email: formattedEvEmail,
              }

              evaluatorUsers.push(evaluator)
              userEvaluators.push(evaluator)
            }

            // Validar que no haya más de 12 evaluadores por usuario
            if (validEvaluatorCount > 12) {
              const userIdentifier = `${firstName} ${lastName} (${email})`
              usersExceedingEvaluatorLimit.push(userIdentifier)
              continue // Continuar con el siguiente usuario sin procesar este
            }

            // Verificar que el usuario tenga al menos un evaluador válido
            if (!hasValidEvaluator) {
              validationErrors.usersWithoutValidEvaluators++
              continue
            }

            // Verificar que el usuario evaluado sea válido antes de agregarlo
            if (!userIsValid) {
              continue
            }

            // Ya no hay tipo de documento o número de documento para los evaluados
            const evaluatedPerson: any = {
              evaluated_role: evaluatedRole,
              first_name: capitalizeWords(firstName.toString() || ''),
              last_name: capitalizeWords(lastName.toString() || ''),
              email: formatEmail(email),
              evaluators: userEvaluators,
            }

            // Verificar si ya existe un usuario con el mismo email en el array actual
            const existingUserIndex = evaluatedUsers.findIndex(
              (existingUser: any) => existingUser.email === evaluatedPerson.email
            )

            if (existingUserIndex >= 0) {
              // Si el usuario ya existe, combinar evaluadores evitando duplicados
              const existingUser = evaluatedUsers[existingUserIndex]
              const existingEvaluatorEmails = new Set(existingUser.evaluators.map((evaluator: any) => evaluator.email))
              const newEvaluators = userEvaluators.filter(
                (evaluator: any) => !existingEvaluatorEmails.has(evaluator.email)
              )

              // Actualizar el usuario existente con los nuevos evaluadores únicos
              evaluatedUsers[existingUserIndex] = {
                ...existingUser,
                evaluators: [...existingUser.evaluators, ...newEvaluators],
              }
            } else {
              // Si no existe, agregarlo normalmente
              evaluatedUsers.push(evaluatedPerson)
            }
          }

          // Validar usuarios que superan el límite de 12 evaluadores
          if (usersExceedingEvaluatorLimit.length > 0) {
            getWarning(
              `Se encontraron ${usersExceedingEvaluatorLimit.length} usuario${
                usersExceedingEvaluatorLimit.length > 1 ? 's' : ''
              } con más de 12 evaluadores asignados. El máximo permitido es 12 evaluadores por usuario. Por favor, revisa el archivo y vuelve a intentarlo.`
            )
            // Limpiar el archivo del modal y cancelar la subida
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            setFileList([])
            setUsers([])
            setEvaluators([])
            setFileExist(false)
            return
          }

          // Validar que no haya más de 30 usuarios para evaluar
          if (evaluatedUsers.length > 30) {
            getWarning(
              `El archivo contiene ${evaluatedUsers.length} usuarios para evaluar. El máximo permitido es 30 usuarios. Por favor, reduce la cantidad de usuarios en el archivo y vuelve a intentarlo.`
            )
            // Limpiar el archivo del modal
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            setFileList([])
            setUsers([])
            setEvaluators([])
            setFileExist(false)
            return
          }

          // Mostrar errores de validación si los hay
          const hasValidationErrors = Object.values(validationErrors).some((count) => count > 0)

          if (hasValidationErrors) {
            const errorMessages: string[] = []

            // Errores de usuarios
            if (validationErrors.usersWithMissingEvaluatedRole > 0) {
              errorMessages.push(
                `${validationErrors.usersWithMissingEvaluatedRole} usuario${
                  validationErrors.usersWithMissingEvaluatedRole > 1 ? 's' : ''
                } sin cargo del evaluado`
              )
            }
            if (validationErrors.usersWithMissingEmail > 0) {
              errorMessages.push(
                `${validationErrors.usersWithMissingEmail} usuario${
                  validationErrors.usersWithMissingEmail > 1 ? 's' : ''
                } con email faltante`
              )
            }
            if (validationErrors.usersWithInvalidEmail > 0) {
              errorMessages.push(
                `${validationErrors.usersWithInvalidEmail} usuario${
                  validationErrors.usersWithInvalidEmail > 1 ? 's' : ''
                } con email inválido`
              )
            }
            if (validationErrors.usersWithMissingFirstName > 0) {
              errorMessages.push(
                `${validationErrors.usersWithMissingFirstName} usuario${
                  validationErrors.usersWithMissingFirstName > 1 ? 's' : ''
                } sin nombre`
              )
            }
            if (validationErrors.usersWithInvalidFirstName > 0) {
              errorMessages.push(
                `${validationErrors.usersWithInvalidFirstName} usuario${
                  validationErrors.usersWithInvalidFirstName > 1 ? 's' : ''
                } con nombre inválido (solo puede contener letras)`
              )
            }
            if (validationErrors.usersWithMissingLastName > 0) {
              errorMessages.push(
                `${validationErrors.usersWithMissingLastName} usuario${
                  validationErrors.usersWithMissingLastName > 1 ? 's' : ''
                } sin apellido`
              )
            }
            if (validationErrors.usersWithInvalidLastName > 0) {
              errorMessages.push(
                `${validationErrors.usersWithInvalidLastName} usuario${
                  validationErrors.usersWithInvalidLastName > 1 ? 's' : ''
                } con apellido inválido (solo puede contener letras)`
              )
            }
            if (validationErrors.usersWithoutValidEvaluators > 0) {
              errorMessages.push(
                `${validationErrors.usersWithoutValidEvaluators} usuario${
                  validationErrors.usersWithoutValidEvaluators > 1 ? 's' : ''
                } sin evaluadores válidos`
              )
            }

            // Errores de evaluadores
            if (validationErrors.evaluatorsWithMissingEmail > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithMissingEmail} evaluador${
                  validationErrors.evaluatorsWithMissingEmail > 1 ? 'es' : ''
                } sin email`
              )
            }
            if (validationErrors.evaluatorsWithInvalidEmail > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithInvalidEmail} evaluador${
                  validationErrors.evaluatorsWithInvalidEmail > 1 ? 'es' : ''
                } con email inválido`
              )
            }
            if (validationErrors.evaluatorsWithMissingRole > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithMissingRole} evaluador${
                  validationErrors.evaluatorsWithMissingRole > 1 ? 'es' : ''
                } sin rol`
              )
            }
            if (validationErrors.evaluatorsWithMissingFirstName > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithMissingFirstName} evaluador${
                  validationErrors.evaluatorsWithMissingFirstName > 1 ? 'es' : ''
                } sin nombre`
              )
            }
            if (validationErrors.evaluatorsWithInvalidFirstName > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithInvalidFirstName} evaluador${
                  validationErrors.evaluatorsWithInvalidFirstName > 1 ? 'es' : ''
                } con nombre inválido (solo puede contener letras)`
              )
            }
            if (validationErrors.evaluatorsWithMissingLastName > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithMissingLastName} evaluador${
                  validationErrors.evaluatorsWithMissingLastName > 1 ? 'es' : ''
                } sin apellido`
              )
            }
            if (validationErrors.evaluatorsWithInvalidLastName > 0) {
              errorMessages.push(
                `${validationErrors.evaluatorsWithInvalidLastName} evaluador${
                  validationErrors.evaluatorsWithInvalidLastName > 1 ? 'es' : ''
                } con apellido inválido (solo puede contener letras)`
              )
            }

            const errorMessage = `Se encontraron problemas en el archivo Excel: ${errorMessages.join(', ')}`
            getWarning(errorMessage)

            // Limpiar el archivo del modal y cancelar la subida
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
            setFileList([])
            setUsers([])
            setEvaluators([])
            setFileExist(false)
            return
          }

          const processedEvaluatedUsers = evaluatedUsers.map((user) => {
            const processedUser = { ...user }
            if (processedUser.evaluators) {
              processedUser.evaluators = processedUser.evaluators.map((evaluator: any) => {
                const processedEvaluator = { ...evaluator }
                ;(Object.keys(processedEvaluator) as Array<keyof typeof processedEvaluator>).forEach((key) => {
                  if (typeof processedEvaluator[key] === 'string') {
                    if (key === 'email' || key === 'document') {
                      processedEvaluator[key] = (processedEvaluator[key] as string).replace(/\s+/g, '')
                    } else {
                      processedEvaluator[key] = (processedEvaluator[key] as string).trim()
                    }
                  }
                })
                return processedEvaluator
              })
            }

            ;(Object.keys(processedUser) as Array<keyof User>).forEach((key) => {
              if (typeof processedUser[key] === 'string') {
                if (key === 'email' || key === 'document') {
                  ;(processedUser as any)[key] = (processedUser[key] as string).replace(/\s+/g, '')
                } else {
                  ;(processedUser as any)[key] = (processedUser[key] as string).trim()
                }
              }
            })
            return processedUser
          })

          if (canAddUsers) {
            const existingUsers = addedUsers360 || []
            const existingEvaluators = addedEvaluators || []

            const combinedUsers = [...existingUsers]
            const combinedEvaluators = [...existingEvaluators]

            processedEvaluatedUsers.forEach((newUser: any) => {
              const existingUserIndex = combinedUsers.findIndex(
                (existingUser: any) => existingUser.email === newUser.email
              )

              if (existingUserIndex >= 0) {
                const existingUser = combinedUsers[existingUserIndex]
                const userExistingEvaluators = existingUser.evaluators || []

                const existingEvaluatorEmails = new Set(userExistingEvaluators.map((evaluator: any) => evaluator.email))
                const newEvaluators = (newUser.evaluators || []).filter(
                  (evaluator: any) => !existingEvaluatorEmails.has(evaluator.email)
                )

                combinedUsers[existingUserIndex] = {
                  ...existingUser,
                  evaluators: [...userExistingEvaluators, ...newEvaluators],
                }

                // Agregar nuevos evaluadores únicos a la lista global
                newEvaluators.forEach((newEval: any) => {
                  const globalEvalExists = combinedEvaluators.some(
                    (evaluator: any) => evaluator.email === newEval.email
                  )
                  if (!globalEvalExists) {
                    combinedEvaluators.push(newEval)
                  }
                })
              } else {
                const isDuplicateEmail = combinedUsers.some((existingUser: any) => existingUser.email === newUser.email)

                if (!isDuplicateEmail) {
                  combinedUsers.push(newUser)

                  if (newUser.evaluators) {
                    newUser.evaluators.forEach((evaluator: any) => {
                      const globalEvalExists = combinedEvaluators.some(
                        (existingEval: any) => existingEval.email === evaluator.email
                      )
                      if (!globalEvalExists) {
                        combinedEvaluators.push(evaluator)
                      }
                    })
                  }
                }
              }
            })

            const uniqueUsers = combinedUsers.reduce((acc: any[], current: any) => {
              const isDuplicate = acc.some((user: any) => user.email === current.email)
              if (!isDuplicate) {
                acc.push(current)
              }
              return acc
            }, [])

            const uniqueEvaluators = combinedEvaluators.reduce((acc: any[], current: any) => {
              const isDuplicate = acc.some((evaluator: any) => evaluator.email === current.email)
              if (!isDuplicate) {
                acc.push(current)
              }
              return acc
            }, [])

            setUsers(uniqueUsers)
            setEvaluators(uniqueEvaluators)
          } else {
            // Si canAddUsers es false, reemplazar usuarios existentes (comportamiento por defecto)
            const processedEvaluatorUsers = evaluatorUsers.map((user) => {
              const processedUser = { ...user }
              ;(Object.keys(processedUser) as Array<keyof User>).forEach((key) => {
                if (typeof processedUser[key] === 'string') {
                  if (key === 'email' || key === 'document') {
                    ;(processedUser as any)[key] = (processedUser[key] as string).replace(/\s+/g, '')
                  } else {
                    ;(processedUser as any)[key] = (processedUser[key] as string).trim()
                  }
                }
              })
              return processedUser
            })

            setUsers(processedEvaluatedUsers)
            setEvaluators(processedEvaluatorUsers)
          }
        }
      }

      reader.readAsArrayBuffer(fileList[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList])

  useEffect(() => {
    if (showStepTwo) {
      setStepOne(false)
      setStepTwo(true)
    }
  }, [showStepTwo])

  const handleNext = () => {
    setStepOne(false)
    setStepTwo(true)
  }

  const handleBack = () => {
    setStepOne(true)
    setStepTwo(false)
  }

  const handleFiles = (files: FileList) => {
    setFileList(Array.from(files))
  }

  const clearUsers = () => {
    setUsers([])
    setEvaluators([])
  }

  const clearInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setFileList([])
    clearUsers()
    setFileExist(false)
  }

  const handleImport = () => {
    setOpenModal(false)

    if (test360Data.length) {
      const uniqueEvaluators = evaluators.reduce((acc: EvaluatorProps[], current) => {
        const key = current.email
        const isDuplicate = acc.some((item) => item.email === key)

        if (!isDuplicate) {
          acc.push(current)
        }
        return acc
      }, [])

      setEvaluators(uniqueEvaluators)

      if (uniqueEvaluators.length === 0) {
        getError('Por favor, agrega al menos un evaluador para cada usuario.')
        clearInput()
        return
      }
      if (users.length === 0) {
        getError('Por favor, agrega al menos un usuario.')
        clearInput()
        return
      }

      // Validar que todos los usuarios tengan datos completos
      const usersWithIncompleteData = users.filter((user) => !user.email || !user.first_name || !user.last_name)

      if (usersWithIncompleteData.length > 0) {
        const errorMsg = 'Hay usuarios con datos incompletos. Todos los usuarios deben tener email, nombre y apellido.'
        getError(errorMsg)
        clearInput()
        return
      }

      // Validar que cada usuario tenga al menos un evaluador
      const usersWithoutEvaluators = users.filter((user) => !user.evaluators || user.evaluators.length === 0)

      if (usersWithoutEvaluators.length > 0) {
        const errorMsg = 'Hay usuarios sin evaluadores asignados. Cada usuario debe tener al menos un evaluador.'
        getError(errorMsg)
        clearInput()
        return
      }

      // Validar que los evaluadores tengan datos completos
      let hasInvalidEvaluator = false
      const invalidEvaluatorDetails: string[] = []

      users.forEach((user) => {
        if (user.evaluators) {
          user.evaluators.forEach((evaluator) => {
            if (!evaluator.email || !evaluator.first_name || !evaluator.last_name) {
              hasInvalidEvaluator = true
              const userIdentifier = user.email || `${user.first_name} ${user.last_name}`
              const missing = []
              if (!evaluator.email) missing.push('email')
              if (!evaluator.first_name) missing.push('nombre')
              if (!evaluator.last_name) missing.push('apellido')

              invalidEvaluatorDetails.push(`Usuario ${userIdentifier} tiene un evaluador sin ${missing.join(', ')}`)
            }
          })
        }
      })

      if (hasInvalidEvaluator) {
        getError(`Se encontraron evaluadores con datos incompletos:\n${invalidEvaluatorDetails.join('\n')}`)
        clearInput()
        return
      }

      // Validación para que un evaluador no evalúe a más de 30 usuarios
      const evaluatorCount: { [email: string]: number } = {}

      // Contamos cuántos usuarios evalúa cada evaluador
      users.forEach((user) => {
        if (user.evaluators && user.evaluators.length > 0) {
          user.evaluators.forEach((evaluator) => {
            if (evaluator.email) {
              if (!evaluatorCount[evaluator.email]) {
                evaluatorCount[evaluator.email] = 1
              } else {
                evaluatorCount[evaluator.email] += 1
              }
            }
          })
        }
      })

      // Verificamos si algún evaluador supera el límite de 30 evaluados
      const evaluatorsExceedingLimit = Object.entries(evaluatorCount)
        .filter(([, count]) => count > 30)
        .map(([email]) => email)

      if (evaluatorsExceedingLimit.length > 0) {
        // Formateamos el mensaje para mostrar cada evaluador con su cantidad de evaluados
        const evaluatorsList = evaluatorsExceedingLimit
          .map((email) => {
            const count = evaluatorCount[email]
            return `${email} (${count} usuarios)`
          })
          .join(', ')

        getWarning(
          `Los siguientes evaluadores están asignados a más de 30 usuarios: ${evaluatorsList}. Por favor, reduce la cantidad de usuarios asignados a estos evaluadores.`
        )
        clearInput()
        return
      }

      if (setAddTest360FromExcel) {
        setAddTest360FromExcel({ users, evaluators: uniqueEvaluators })
        clearInput()
        return
      }
    }

    // Verificar que todos los usuarios tengan datos completos
    const incompleteUsers = users.filter((userData: any) => {
      const user = userData.user || userData
      return !user.email || !user.first_name || !user.last_name
    })

    if (incompleteUsers.length > 0) {
      getError('Hay usuarios con datos incompletos. Todos los usuarios deben tener email, nombre y apellido.')
      clearInput()
      return
    }

    const usersToImport = users.map(({ user }: any) => user)
    if (setTryUploadAgain) {
      setAddedUsers(usersToImport)
      const uploadedByFile = true
      const tryAgain = true
      navigate(platformAdminRoutes.Resume, {
        state: { data: users, uploadedByFile, tryAgain },
      })
    } else {
      setAddedUsers(usersToImport)
    }
    setUploadedbyFile(true)
    clearInput()
  }

  const exampleFile360 = process.env.REACT_APP_EXCEL_360

  const excelLink = test360Data.length
    ? `${exampleFile360}`
    : 'https://bluesitestorage.s3.amazonaws.com/plantilla_de_usuarios.xlsx'

  return (
    <Modal
      hideCloseButton={true}
      show={openModal}
      setShow={setOpenModal}
      styleContent={{
        width: '80%',
        height: 'auto',
        borderRadius: '10px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: '#FFFFFF',
      }}
    >
      <ModalContainer>
        <ModalHeader>
          <div
            style={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setOpenModal(false)
            }}
          >
            <CloseIcon />
          </div>
        </ModalHeader>

        <ModalBody>
          <H4 style={{ textAlign: 'start' }}>Importar usuarios </H4>
          {stepOne ? (
            <>
              {!test360Data.length ? (
                <p>
                  Descarga la plantilla y llena la columna A con el tipo de documento, la columna B con el número de
                  documento, la columna C con el correo electrónico, la columna D con nombre del usuario y la columna E
                  con su apellido.
                </p>
              ) : (
                <Row flexDirection="column" style={{ paddingTop: '10px' }}>
                  <H5 style={{ fontWeight: '400' }}>
                    Descarga la plantilla y llena las columnas con los datos solicitados de los usuarios a quienes
                    deseas asignar las pruebas.
                  </H5>
                  <H5>Sugerencias:</H5>
                  <ol style={{ margin: '0 0 20px 0' }}>
                    <li>Evita los espacios en las celdas.</li>
                    <li>Asegúrate de seleccionar los roles de los evaluadores de forma correcta.</li>
                    <li>Puedes dejar vacías las columnas de evaluadores que no necesites.</li>
                    <li>Verifica que un mismo evaluador no esté asignado más de una vez al mismo evaluado</li>
                  </ol>
                </Row>
              )}
              <Row
                justifyContent="center"
                flexDirection="column"
                style={{
                  boxSizing: 'border-box',
                  alignContent: 'center',
                  background: ' #919EAB1F',
                  borderRadius: 12,
                  padding: 50,
                }}
              >
                <AnchorSection href={excelLink} download>
                  <Row gap="10px" alignItems="center" style={{ alignSelf: 'center' }}>
                    <img src={File} alt="file" />
                    <H4>Plantilla de usuarios.xlxs</H4>
                  </Row>
                  <Row gap="10px" alignItems="center">
                    <DownloadIcon /> <span style={{ color: '#2D749C' }}> Descargar archivo</span>
                  </Row>
                </AnchorSection>
              </Row>
              <Row justifyContent="flex-end" alignItems="center">
                <Button
                  onClick={() => {
                    handleNext()
                  }}
                >
                  {t('global.common.nextButtonText')}
                </Button>
              </Row>
            </>
          ) : null}
          {stepTwo ? (
            <>
              <H5 style={{ fontWeight: 'normal', marginTop: '10px' }}>
                Adjunta aquí la plantilla llena con los datos de los usuarios a quienes deseas asignar pruebas
              </H5>
              <Row gap="10px" flexDirection="column">
                <Row
                  justifyContent="center"
                  flexDirection="column"
                  style={{
                    alignContent: 'center',
                    background: ' #919EAB1F',
                    borderRadius: 12,
                    padding: 50,
                    boxSizing: 'border-box',
                  }}
                >
                  <Row
                    alignItems="center"
                    gap="10px"
                    className={`file-dropzone ${dragging ? 'dragging' : ''}`}
                    onDrop={(e) => handleDrop(e, fileInputRef, handleFiles, setDragging, setFileExist)}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, setDragging)}
                    onDragLeave={(e) => handleDragLeave(e, setDragging)}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{ alignSelf: 'center' }}
                  >
                    <FileFolderIcon />
                    <div>
                      <span style={{ fontSize: 20, fontWeight: 'bold', color: ' #1F2124' }}>
                        Selecciona los archivos
                      </span>
                      <p style={{ fontSize: 18, color: ' #1F2124' }}>
                        Arrastra los archivos aquí o has click para buscarlos en tu computador
                      </p>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx, .xlsm, .xls"
                      onChange={(e) => {
                        if (e.target.files) {
                          const file = e.target.files[0]
                          const fileExtension = file?.name.split('.').pop()?.toLowerCase()

                          if (!file?.name) setFileExist(false)
                          else setFileExist(true)

                          if (fileExtension !== 'xls' && fileExtension !== 'xlsx' && fileExtension !== 'xlsm') {
                            getError('Solo se pueden subir archivos de Excel (.xls, .xlsx, .xlsm)')
                            return
                          }
                          handleFiles(e.target.files)
                        }
                      }}
                      className="file-input"
                      style={{ display: 'none' }}
                    />
                  </Row>
                </Row>
                {fileList.length > 0 ? (
                  <Row
                    justifyContent="space-between"
                    alignItems="center"
                    style={{
                      boxSizing: 'border-box',
                      alignContent: 'center',
                      background: ' #919EAB1F',
                      borderRadius: 12,
                      padding: 10,
                    }}
                  >
                    <Row gap="10px" alignItems="center" style={{ paddingTop: 0, paddingBottom: 0 }}>
                      <GreenFile />
                      {fileList.map((file, index) => (
                        <Row flexDirection="column" gap="5px" style={{ paddingTop: 0, paddingBottom: 0 }} key={index}>
                          <span>{file.name}</span>
                          <span>{file.size} bytes</span>
                        </Row>
                      ))}
                    </Row>
                    <div style={{ cursor: 'pointer' }} onClick={clearInput}>
                      <DeleteIcon />
                    </div>
                  </Row>
                ) : null}
                <Row justifyContent="space-between" alignItems="center">
                  <span
                    style={{ color: '#2D749C', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => {
                      handleBack()
                    }}
                  >
                    Atrás
                  </span>
                  <Button
                    disabled={!fileExist}
                    onClick={() => {
                      handleImport()
                    }}
                  >
                    Importar
                  </Button>
                </Row>
              </Row>
            </>
          ) : null}
        </ModalBody>
      </ModalContainer>
    </Modal>
  )
}
export default UploadModal
